import React, { FC, useEffect, useState, useRef } from 'react';
import { Formik, Form, FormikHelpers } from 'formik';
import { IActionvationPreferenceForm } from 'core/interface/activation';
import { IAuthSteps } from 'core/interface/authSteps';
import { ActivationPageEnum } from 'core/enum/activationPage';
import {
  availableAbout,
  availableRelocate,
  candidateWorkHours,
  manageExperience,
  monthlySalary,
  workExperience,
  hourlyRateChoices
} from 'core/constants/preferenceConstats';
import DropDown from 'components/Dropdown';
import Checkboxes from 'components/shared/Checkbox/Index';
import Button from 'components/Button/Index';
import { RootState } from '../../../../redux/store';
import Activation from '../Index';
import { initialValues, validationSchema } from './form';
import { useSelector } from 'react-redux';
import { ICheckboxItem } from 'core/interface/checkboxes';
import Multiselect from 'components/shared/Multiselect/Index';
import { OptionType } from 'core/interface/autocomplete';
import Image from 'components/image/Image';
import { CandidateService } from 'services/candidate/candidate';
import { IMe } from 'core/interface/me';
import Autocomplete from 'components/shared/Autocomplete/Index';
import { ILocationRedux, IndustryRedux } from 'core/interface/education';
import { JobService } from 'services/jobs/job';
import { useDebounce } from 'helpers/useDebounce';
import RadioButton from 'components/RadioGroup/Index';

import './Index.scss';

const MatchPreferences: FC<IAuthSteps> = ({
  t,
  onClick,
  showTopSide,
  isOneBtn = false
}): JSX.Element => {
  const me = useSelector((state: IMe) => state.user).user;
  const { candidates } = useSelector((state: RootState) => state.candidates);

  const industries = useSelector(
    (selector: IndustryRedux) => selector.industries?.industry || []
  );
  const cities = useSelector(
    (selector: ILocationRedux) => selector.locations?.cities || []
  );
  const [selectedInterests, setSelectedInterests] = useState<string[]>([]);
  const [loader, setLoader] = useState<boolean>(false);
  const [timeoutFunc, setTimeoutFunc] = useState<NodeJS.Timeout | null>(null);
  const [locationQuery, setLocationQuery] = useState<string>('');
  const debouncedLocationQuery = useDebounce(locationQuery, 500);

  const [initialVal, setInitialVal] =
    useState<IActionvationPreferenceForm>(initialValues);
  const formikRef = useRef<any>(null);

  const handleInputChange = async (e: string): Promise<void> => {
    if (timeoutFunc) {
      clearTimeout(timeoutFunc);
    }
    const timeout = setTimeout(() => {
      JobService.getLocations({ q: e ?? '' });
    }, 500);
    setTimeoutFunc(timeout);
  };

  useEffect(() => {
    setInitialVal({
      ...initialVal,
      availability:
        candidates.availability !== undefined &&
        candidates.availability !== null
          ? String(candidates.availability)
          : '',
      contract: Array.isArray(candidates.contract) ? candidates.contract : [],
      expect_per_month:
        candidates.salary !== undefined && candidates.salary !== null
          ? String(candidates.salary)
          : '',
      hourly_rate:
        candidates.hourly_rate !== undefined && candidates.hourly_rate !== null
          ? String(candidates.hourly_rate)
          : '',
      industries: Array.isArray(candidates?.industries)
        ? candidates.industries.map((ind: { id: any }) => ind.id)
        : [],
      interests: Array.isArray(candidates?.interests)
        ? candidates.interests.map((el: string) => ({ title: el }))
        : [],
      location:
        candidates.locations && candidates.locations.length > 0
          ? candidates.locations[0]
          : { display_name: '', id: -1 },
      management_exp:
        candidates.management_exp !== undefined &&
        candidates.management_exp !== null
          ? String(candidates.management_exp)
          : '',
      willing_to_move:
        candidates.relocation !== undefined && candidates.relocation !== null
          ? String(candidates.relocation)
          : '',
      work_exp:
        candidates.work_exp !== undefined && candidates.work_exp !== null
          ? String(candidates.work_exp)
          : '',
      work_hours:
        candidates.salary !== undefined && candidates.salary !== null
          ? String(candidates.salary)
          : '',
      remote_only: me?.remote_only || false
    });
    setSelectedInterests(() =>
      Array.isArray(candidates?.interests)
        ? candidates.interests.map((el: string) => el)
        : []
    );
    if (formikRef.current) {
      formikRef.current.validateForm();
      formikRef.current.setTouched({
        availability: true,
        willing_to_move: true,
        work_exp: true,
        work_hours: true,
        expect_per_month: true,
        management_exp: true,
        industries: true,
        location: true,
        contract: true
      });
    }
  }, [candidates]);

  useEffect(() => {
    if (debouncedLocationQuery) {
      JobService.getLocations({ q: debouncedLocationQuery });
    }
  }, [debouncedLocationQuery]);

  const handleSubmit = async (
    values: IActionvationPreferenceForm,
    actions: FormikHelpers<IActionvationPreferenceForm>
  ): Promise<void> => {
    setLoader(true);
    const body = {
      availability: +values.availability,
      work_hours: +values.work_hours,
      relocation: +values.willing_to_move,
      work_exp: +values.work_exp,
      management_exp: +values.management_exp,
      salary: +values.expect_per_month,
      ...(values.contract.includes(2) &&
        values.hourly_rate && { hourly_rate: +values.hourly_rate || 0 }),
      locations: [values.location],
      industries: values.industries,
      interests: selectedInterests.map((el) => el),
      contract: values.contract,
      default_lang: 1,
      remote_only: values.remote_only
    };

    await CandidateService.updateCandidate(body, me.candidate_id);
    onClick(ActivationPageEnum.EDUCATION);
    setLoader(false);
  };

  const handleInterestChange = (interest: string | OptionType[]): void => {
    if (typeof interest != 'string' && interest) {
      setSelectedInterests((prev) => {
        const prevArray = Array.isArray(prev) ? prev : [];
        const interests = interest.map((el) => el.title as string);
        return [...new Set([...prevArray, ...interests])];
      });
    }
  };

  const handleRemoveInterets = (e: string): void => {
    setSelectedInterests((prev) => prev.filter((ind) => ind !== e));
  };

  const handleLoadMoreCities = () => {
    const params = { limit: 10, offset: cities.length };
    JobService.getLocations(params, true);
  };

  const handleLocationChange = (
    e: string,
    setFieldValue: (
      field: string,
      value: any,
      shouldValidate?: boolean
    ) => void,
    setErrors: (errors: any) => void
  ) => {
    setLocationQuery(e);
    if (cities.find((cit) => cit.display_name === e)) {
      setFieldValue('location', {
        id: cities.find((city) => city.display_name === e)?.id,
        display_name: cities.find((city) => city.display_name === e)
          ?.display_name
      });
    } else {
      setErrors({
        location: { display_name: t('Invalid Name') }
      });
    }
  };

  return (
    <Activation
      style={{
        maxHeight: '90vh',
        marginBottom: '20px'
      }}
      showTopSide={showTopSide}
      title={t('Indicate your match preferences')}
      currentStep={2}
    >
      <div className="preference">
        <Formik
          innerRef={formikRef}
          enableReinitialize={true}
          initialValues={initialVal}
          onSubmit={(values, actions) => handleSubmit(values, actions)}
          validationSchema={validationSchema(t)}
          validateOnMount={true}
        >
          {({
            setFieldValue,
            errors,
            values,
            touched,
            isValid,
            setErrors,
            setFieldTouched
          }) => {
            const selectedIndustryObjects = industries
              .filter((ind) => values.industries.includes(ind.id))
              .map((ind) => ({
                id: ind.id,
                name: ind.name,
                title: ind.name
              }));

            return (
              <Form>
                <div className="form-body scrollable">
                  <div className="top">
                    <div className="fields">
                      <span className="label">
                        {t('When will you be available to start a new job?')}
                      </span>
                      <DropDown
                        fieldName="availability"
                        defaultValue={values.availability}
                        placeholder={t('Available in...')}
                        error={
                          touched.availability && errors.availability
                            ? errors.availability
                            : ''
                        }
                        dropdownClassName="dropdown"
                        items={availableAbout}
                        selectedValue={(e) => {
                          setFieldTouched('availability', true);
                          setFieldValue('availability', e);
                        }}
                      />
                    </div>
                    <div className="fields willing">
                      <span className="label">
                        {t('Are you willing to move?')}
                      </span>
                      <DropDown
                        fieldName="willing_to_move"
                        defaultValue={values.willing_to_move}
                        error={
                          touched.willing_to_move && errors.willing_to_move
                            ? errors.willing_to_move
                            : ''
                        }
                        placeholder={t('Willing to move')}
                        dropdownClassName="dropdown"
                        items={availableRelocate}
                        selectedValue={(e) => {
                          setFieldTouched('willing_to_move', true);
                          setFieldValue('willing_to_move', e);
                        }}
                      />
                    </div>
                  </div>
                  <div className="fields">
                    <span className="label">{t('Work experience')}</span>
                    <DropDown
                      fieldName="work_exp"
                      defaultValue={values.work_exp}
                      placeholder={t('Work experience')}
                      error={
                        touched.work_exp && errors.work_exp
                          ? errors.work_exp
                          : ''
                      }
                      dropdownClassName="dropdown"
                      items={workExperience}
                      selectedValue={(e) => {
                        setFieldTouched('work_exp', true);
                        setFieldValue('work_exp', e);
                      }}
                    />
                  </div>
                  <div className="fields">
                    <span className="label">{t('Work hours')}</span>
                    <DropDown
                      defaultValue={values.work_hours}
                      dropdownClassName="dropdown"
                      items={candidateWorkHours}
                      selectedValue={(e) => {
                        setFieldTouched('work_hours', true);
                        setFieldValue('work_hours', e);
                      }}
                      fieldName="work_hours"
                      placeholder={t('Work hours')}
                      error={
                        touched.work_hours && errors.work_hours
                          ? errors.work_hours
                          : ''
                      }
                    />
                  </div>
                  <div className="checkboxes">
                    <span className="label">
                      {t('Preferred contract (multiple selection)')}
                    </span>
                    <Checkboxes
                      onArrayChange={(e: ICheckboxItem | ICheckboxItem[]) => {
                        if (Array.isArray(e)) {
                          setFieldValue(
                            'contract',
                            e
                              .filter((item) => item.isChecked)
                              .map((item) => Number(item.value))
                          );
                        } else {
                          setFieldValue('contract', e.value);
                        }
                      }}
                      onChange={(e) => e}
                      className="contract"
                      checkboxes={[
                        {
                          label: t('Permanent'),
                          value: '1',
                          isChecked:
                            values.contract && values.contract.includes(1)
                        },
                        {
                          label: t('Freelance'),
                          value: '2',
                          isChecked:
                            values.contract && values.contract.includes(2)
                        },
                        {
                          label: t('Internship'),
                          value: '3',
                          isChecked:
                            values.contract && values.contract.includes(3)
                        }
                      ]}
                    />
                  </div>
                  <div className="fields">
                    <span className="label">
                      {t('What do you expect to earn per month?')}
                    </span>
                    <DropDown
                      fieldName="expect_per_month"
                      defaultValue={values.expect_per_month}
                      placeholder={t('Salary expectation')}
                      error={
                        touched.expect_per_month && errors.expect_per_month
                          ? errors.expect_per_month
                          : ''
                      }
                      dropdownClassName="dropdown"
                      items={monthlySalary}
                      selectedValue={(e) => {
                        setFieldTouched('expect_per_month', true);
                        setFieldValue('expect_per_month', e);
                      }}
                    />
                    {values.contract && values.contract.includes(2) && (
                      <>
                        <span className="label">
                          {t('What do you expect to earn per hour?')}
                        </span>

                        <DropDown
                          fieldName="hourly_rate"
                          defaultValue={values.hourly_rate}
                          placeholder={t('Hourly rate expectation')}
                          error={
                            touched.hourly_rate && errors.hourly_rate
                              ? errors.hourly_rate
                              : ''
                          }
                          dropdownClassName="dropdown"
                          items={hourlyRateChoices}
                          selectedValue={(e) => {
                            setFieldTouched('hourly_rate', true);
                            setFieldValue('hourly_rate', e);
                          }}
                        />
                      </>
                    )}
                  </div>
                  <div className="fields loc">
                    <span className="label">
                      {t('Preferred work location (city)')}
                    </span>
                    <Autocomplete
                      loadMore={handleLoadMoreCities}
                      fieldName="location"
                      className={`inputs `}
                      value={initialVal.location?.display_name || ''}
                      options={
                        cities.map((item, index) => ({
                          key: index,
                          id: item.id,
                          title: item.display_name
                        })) || []
                      }
                      placeholder={t('Paris, Amsterdam, newyorkcity...')}
                      show_location={false}
                      onInputChange={(e) => {
                        handleLocationChange(e, setFieldValue, setErrors);
                      }}
                      values={cities || []}
                    />
                  </div>
                  <div className="fields">
                    <span className="label">{t('Managerial experience')}</span>
                    <DropDown
                      defaultValue={values.management_exp}
                      placeholder={t('Managerial experience')}
                      error={
                        touched.management_exp && errors.management_exp
                          ? errors.management_exp
                          : ''
                      }
                      dropdownClassName="dropdown"
                      items={manageExperience}
                      selectedValue={(e) => {
                        setFieldTouched('management_exp', true);
                        setFieldValue('management_exp', e);
                      }}
                    />
                  </div>
                  <div className="fields">
                    <span className="label">{t('Remote work only')}</span>
                    <RadioButton
                      buttons={[
                        {
                          label: t('Yes'),
                          value: '1',
                          isChecked: values.remote_only
                        },
                        {
                          label: t('No'),
                          value: '0',
                          isChecked: !values.remote_only
                        }
                      ]}
                      onChange={(e) => {
                        setFieldValue('remote_only', e === '1' ? true : false);
                      }}
                    />
                  </div>
                  <div className="fields">
                    <span className="label">{t('Industry preferences')}</span>
                    <Multiselect
                      fieldName="industries"
                      value={selectedIndustryObjects}
                      className={`${errors.industries ? 'error' : ''}`}
                      placeholder={t('Industry')}
                      show_location={false}
                      onChange={(e: string | OptionType[]) => {
                        if (typeof e !== 'string') {
                          setFieldValue(
                            'industries',
                            e.map((ind) => ind.id)
                          );
                        }
                      }}
                      options={
                        industries?.map((e) => ({
                          id: e?.id,
                          title: e?.name
                        })) || []
                      }
                    />

                    <div className="ind">
                      {selectedIndustryObjects &&
                        selectedIndustryObjects.map((industry, index) => (
                          <span
                            className="industry-selected"
                            key={industry.title}
                          >
                            {industry.title}{' '}
                            <span
                              onClick={() => {
                                setFieldValue(
                                  'industries',
                                  values.industries.filter(
                                    (id) => id !== industry.id
                                  )
                                );
                              }}
                            >
                              <Image
                                className="remove"
                                name="removes.svg"
                                width={10}
                                height={10}
                              />
                            </span>
                          </span>
                        ))}
                    </div>
                    <span className="error-msg">{errors.industries}</span>
                  </div>
                  <div className="fields">
                    <span className="label">{t('Interested in')}</span>
                    <Multiselect
                      fieldName="interests"
                      value={selectedInterests ?? []}
                      className={`${errors.interests ? 'error' : ''}`}
                      placeholder={t('Interests in')}
                      show_location={false}
                      onChange={(e) => {
                        const interests = typeof e === 'string' ? [e] : e;
                        setSelectedInterests(interests);
                        setFieldValue('interests', interests);
                      }}
                      freeSolo={true}
                    />

                    <div className="ind">
                      {selectedInterests &&
                        selectedInterests.map((interest, index) => (
                          <span className="industry-selected" key={interest}>
                            {interest}{' '}
                            <span
                              onClick={() =>
                                handleRemoveInterets(interest ?? '')
                              }
                            >
                              <Image
                                className="remove"
                                name="removes.svg"
                                width={10}
                                height={10}
                              />
                            </span>
                          </span>
                        ))}
                    </div>
                    <span className="error-msg">{errors.interests}</span>
                  </div>
                </div>
                {isOneBtn ? (
                  <div className="buttons one">
                    <Button
                      isLoading={loader}
                      className={`next button oneBTN ${
                        !isValid ||
                        (values.industries && values.industries.length === 0)
                          ? 'invalid'
                          : 'valid'
                      }`}
                      type="submit"
                      t={t}
                      title="Save"
                    />
                  </div>
                ) : (
                  <div className="buttons">
                    <Button
                      className="back button"
                      t={t}
                      title="back"
                      onClick={() => onClick(ActivationPageEnum.WHO_YOU_ARE)}
                    />

                    <Button
                      isLoading={loader}
                      className={`next button ${
                        !isValid ||
                        (values.industries && values.industries.length === 0)
                          ? 'invalid'
                          : 'valid'
                      }`}
                      type="submit"
                      t={t}
                      title="Next"
                    />
                  </div>
                )}
              </Form>
            );
          }}
        </Formik>
      </div>
    </Activation>
  );
};

export default MatchPreferences;
